import React, { useEffect, useRef, useState } from "react";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";

const POPOVER_Z_INDEX = 1060;
/** Grace period so the cursor can travel the Popper offset gap without closing. */
const HOVER_CLOSE_DELAY_MS = 200;

const getPopperContainer = () =>
  typeof document !== "undefined" ? document.body : null;

const popperConfig = {
  modifiers: [
    {
      name: "offset",
      options: { offset: [0, 8] },
    },
    {
      name: "preventOverflow",
      options: { boundary: "viewport", padding: 8 },
    },
    {
      name: "computeStyles",
      options: { gpuAcceleration: false },
    },
  ],
};

const PopoverContainer = (props) => {
  const {
    children,
    itemComponent,
    isHover,
    placement = "auto",
    cssClass,
    toolTip,
    rootClose = true,
    /** When set with toolTip: hover + click both open the tooltip (click toggles; rootClose dismisses). */
    toolTipHoverAndClick = false,
    /**
     * Delay (ms) before opening on hover. 0 = open immediately (default).
     * Used for schedule shift detail cards so brief hovers do not flash popovers.
     */
    hoverOpenDelayMs = 0,
    /** Optional hover-in / hover-out notifications for callers that prefetch. */
    onHoverStart = null,
    onHoverEnd = null,
    /** Stack above a parent dialog. Defaults to the Bootstrap popover layer. */
    zIndex = POPOVER_Z_INDEX,
  } = props;

  const [showPopover, setShowPopover] = useState(false);
  const containerRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const openTimeoutRef = useRef(null);
  const showPopoverRef = useRef(false);

  useEffect(() => {
    showPopoverRef.current = showPopover;
  }, [showPopover]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    onHoverStart?.();
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    // Already open (e.g. moving from trigger into the card) — keep it open.
    if (showPopoverRef.current) {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
      }
      return;
    }
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    const delay = Number(hoverOpenDelayMs) > 0 ? Number(hoverOpenDelayMs) : 0;
    if (delay > 0) {
      openTimeoutRef.current = setTimeout(() => {
        openTimeoutRef.current = null;
        setShowPopover(true);
      }, delay);
      return;
    }
    setShowPopover(true);
  };

  const handleMouseLeave = () => {
    onHoverEnd?.();
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setShowPopover(false);
      closeTimeoutRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  const overlayProps = {
    container: getPopperContainer,
    popperConfig:
      zIndex === POPOVER_Z_INDEX
        ? popperConfig
        : { ...popperConfig, strategy: "fixed" },
  };

  const toolTipPopover = (
    <Popover
      id="popover-basic"
      className={cssClass}
      style={{
        backgroundColor: "#1f2937",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: "500",
        padding: "0",
        borderRadius: "0.5rem",
        boxShadow:
          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        border: "none",
        maxWidth: "300px",
        zIndex,
      }}
    >
      <Popover.Content
        style={{
          maxWidth: "300px",
          overflowWrap: "break-word",
          lineHeight: "1.6",
          padding: "0.75rem 1rem",
          color: "#ffffff",
          fontSize: "0.875rem",
          fontWeight: "500",
          backgroundColor: "transparent",
        }}
      >
        <div style={{ color: "#ffffff", wordBreak: "break-word" }}>{toolTip}</div>
      </Popover.Content>
    </Popover>
  );

  if (toolTip) {
    if (toolTipHoverAndClick) {
      return (
        <OverlayTrigger
          trigger={["hover", "click"]}
          placement={placement}
          overlay={toolTipPopover}
          rootClose={rootClose}
          {...overlayProps}
        >
          <div className="inline-block cursor-pointer" ref={containerRef}>
            {children}
          </div>
        </OverlayTrigger>
      );
    }
    return (
      <OverlayTrigger
        trigger="manual"
        placement="auto"
        overlay={toolTipPopover}
        show={showPopover}
        {...overlayProps}
      >
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={containerRef}
        >
          {children}
        </div>
      </OverlayTrigger>
    );
  }

  const popover = (
    <Popover
      id="popover-basic"
      className={cssClass}
      style={{ zIndex }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Popover.Content
        style={{
          maxWidth: "500px",
          overflowWrap: "break-word",
          lineHeight: "1.5",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {itemComponent()}
      </Popover.Content>
    </Popover>
  );

  if (isHover) {
    return (
      <OverlayTrigger
        trigger="manual"
        placement={placement}
        overlay={popover}
        show={showPopover}
        {...overlayProps}
      >
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={containerRef}
        >
          {children}
        </div>
      </OverlayTrigger>
    );
  }

  if (placement) {
    return (
      <OverlayTrigger
        trigger="manual"
        placement={placement}
        overlay={popover}
        show={showPopover}
        {...overlayProps}
      >
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={containerRef}
        >
          {children}
        </div>
      </OverlayTrigger>
    );
  }

  return (
    <OverlayTrigger
      trigger="manual"
      placement="auto"
      overlay={popover}
      show={showPopover}
      {...overlayProps}
    >
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={containerRef}
      >
        {children}
      </div>
    </OverlayTrigger>
  );
};

export default PopoverContainer;
